import memoize from 'lodash/memoize';

export type UrlLike = string | URL;

const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:', 'vscode:'];
const pollutionPattern = `\\b(?:constructor|__defineGetter__|__defineSetter__|hasOwnProperty|__lookupGetter__|__lookupSetter__|isPrototypeOf|propertyIsEnumerable|toString|valueOf|__proto__|toLocaleString)\\b`;
const pollutionRegex = new RegExp(pollutionPattern, 'i');
/**
 * Retrieves the protocol from the URL
 * @example
 * ```
 * getUrlProtocol("https://amp.corp.stripe.com"); // returns 'https:'
 * ```
 * @param url
 */
const getUrlProtocol = (url: UrlLike) => {
  if (typeof url === 'string') {
    try {
      const u = new URL(url);
      return u.protocol;
    } catch {
      return;
    }
  }

  return url.protocol;
};

const isUrlProtocolAllowed = (
  url: UrlLike,
  allowedProtocols = ALLOWED_PROTOCOLS,
): boolean => {
  const protocol = getUrlProtocol(url);
  if (!protocol) {
    return true;
  }

  return allowedProtocols.includes(protocol);
};

const parseUrl = (url: string, base?: string) => {
  try {
    return new URL(url, base);
  } catch {
    return undefined;
  }
};

const tryParseURL = (u: UrlLike): URL | undefined => {
  if (typeof u === 'string') {
    const resolved =
      parseUrl(u) ??
      // e.g. when u = /path
      parseUrl(
        u,
        // In SSR context, location is not defined which causes this to throw an error.
        // We only use the value to run the sanity check on the URL and we don't return the value,
        // so it is safe to use a static URL if location is undefined. Using localhost in this case
        typeof location !== 'undefined' ? location.origin : 'http://localhost',
      );
    return resolved;
  }

  return undefined;
};

const containsProtoKeywords = (u: UrlLike) => {
  const parsedUrl = tryParseURL(u);
  if (!parsedUrl) {
    return false;
  }

  const urlParts = [parsedUrl.pathname, parsedUrl.search, parsedUrl.hash];
  return urlParts.some((part) => pollutionRegex.test(part));
};

export const isSafeUrl = memoize(
  (u: UrlLike) => {
    // Prevent memory leaks by clearing the cache periodically
    if ((isSafeUrl.cache as Map<unknown, unknown>)?.size === 1000) {
      isSafeUrl.cache.clear?.();
    }
    if (!isUrlProtocolAllowed(u)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[safe-links] redirect (${u}) blocked. URL has invalid protocol`,
      );
      return false;
    }

    if (containsProtoKeywords(u)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[safe-links] redirect (${u}) blocked. URL contains proto keywords`,
      );
      return false;
    }

    return true;
  },
  (url: UrlLike) => JSON.stringify([url, typeof location !== 'undefined']),
);
